import { useState } from 'react';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { strings, type SessionDTO } from '@singparty/shared';
import { sessionApi } from '@/lib/sessionApi';
import { apiPost } from '@/lib/apiClient';
import { queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

export const Route = createFileRoute('/host/dashboard')({
  loader: ({ context }) => context.queryClient.ensureQueryData(sessionsListQuery),
  component: Dashboard,
});

const sessionsListQuery = {
  queryKey: ['sessions', 'mine'],
  queryFn: () => sessionApi.listMine(),
};

function defaultName(): string {
  const d = new Date();
  const month = d.toLocaleString('en-US', { month: 'short' });
  return strings.dashboard.defaultNameFor(`${month} ${d.getDate()}`);
}

function Dashboard() {
  const { me } = Route.useRouteContext();
  const router = useRouter();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery(sessionsListQuery);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(defaultName());
  const [hostName, setHostName] = useState('');

  const createMut = useMutation({
    mutationFn: (args: { name: string; hostName?: string }) => sessionApi.create(args.name, args.hostName),
    onSuccess: async ({ session }) => {
      await qc.invalidateQueries({ queryKey: ['sessions', 'mine'] });
      await navigate({ to: '/host/console/$sid', params: { sid: session.id } });
    },
  });

  async function signOut() {
    await apiPost('/api/auth/logout');
    // Clear, don't invalidate — the sign-in guard reads `me` via ensureQueryData,
    // which serves stale cache, so an invalidate would leave it seeing the old
    // identity and redirect us right back to the dashboard. clear() also drops
    // this host's session list so the next sign-in can't see it.
    queryClient.clear();
    await router.invalidate();
    await router.navigate({ to: '/singnow' });
  }

  const active = data?.active ?? [];
  const ended = data?.ended ?? [];

  return (
    <div className="mx-auto max-w-[560px] px-4 py-8">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <Logo />
          <p className="mt-1 text-sm text-text-secondary">{me.email}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={signOut}>
          Sign out
        </Button>
      </header>

      {active.length === 0 && !creating && (
        <div className="rounded-card border border-border bg-surface p-8 text-center">
          <p className="mb-1 text-lg font-semibold">{strings.dashboard.emptyTitle}</p>
          <p className="mb-6 text-text-secondary">{strings.dashboard.emptyBody}</p>
          <Button size="lg" onClick={() => setCreating(true)}>
            {strings.dashboard.startParty}
          </Button>
        </div>
      )}

      {(active.length > 0 || creating) && (
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your parties</h2>
          {!creating && <Button onClick={() => setCreating(true)}>{strings.dashboard.startParty}</Button>}
        </div>
      )}

      {creating && (
        <div className="mb-6 space-y-3 rounded-card border border-border bg-surface p-4">
          <div>
            <label className="mb-1 block text-sm text-text-secondary">{strings.dashboard.nameLabel}</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 w-full rounded-pill border border-border bg-bg px-4 text-sm outline-none focus-visible:border-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-text-secondary">Your name (optional)</label>
            <input
              value={hostName}
              onChange={(e) => setHostName(e.target.value)}
              placeholder="Host"
              maxLength={24}
              className="h-11 w-full rounded-pill border border-border bg-bg px-4 text-sm outline-none focus-visible:border-primary"
            />
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              loading={createMut.isPending}
              onClick={() => createMut.mutate({ name: name.trim() || defaultName(), hostName: hostName.trim() || undefined })}
            >
              Create
            </Button>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {active.map((s) => (
          <SessionCard key={s.id} session={s} live onOpen={() => navigate({ to: '/host/console/$sid', params: { sid: s.id } })} />
        ))}
      </div>

      {ended.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-secondary">
            Past parties
          </h2>
          <div className="space-y-2">
            {ended.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between rounded-card border border-border bg-surface px-4 py-3 text-sm text-text-secondary"
              >
                <span>{s.name}</span>
                <span>{new Date(s.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SessionCard({ session, live, onOpen }: { session: SessionDTO; live?: boolean; onOpen: () => void }) {
  return (
    <div className="flex items-center justify-between rounded-card border border-border bg-surface p-4">
      <div>
        <div className="flex items-center gap-2">
          {live && <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />}
          <span className="font-semibold">{session.name}</span>
        </div>
        <div className="mt-1 font-mono text-lg tracking-widest text-primary">{session.code}</div>
      </div>
      <Button onClick={onOpen}>{strings.dashboard.openConsole}</Button>
    </div>
  );
}
